import type { ChessConstructor } from "../chess/chessTypes.ts";
import { safeMove } from "../chess/notation.ts";
import { positionKey } from "../chess/positionKey.ts";
import {
  lessonCourseSchema,
  type LessonCourse,
  type LessonMove,
  type LessonNode,
} from "./schema.ts";
import { unknownConceptTags } from "./ids.ts";

export type GraphIssue = { code: string; message: string };

export function validateCourse(course: LessonCourse, Chess: ChessConstructor): GraphIssue[] {
  const issues: GraphIssue[] = [];
  const parsed = lessonCourseSchema.safeParse(course);
  if (!parsed.success) {
    for (const issue of parsed.error.issues) {
      issues.push({
        code: "schema",
        message: `${issue.path.join(".")}: ${issue.message}`,
      });
    }
    return issues;
  }

  if (!course.nodes[course.rootPositionKey]) {
    issues.push({ code: "root", message: "rootPositionKey is missing from nodes" });
  }

  for (const [key, node] of Object.entries(course.nodes)) {
    if (key !== node.positionKey) {
      issues.push({
        code: "key-mismatch",
        message: `nodes["${key}"] has positionKey ${node.positionKey}`,
      });
    }
    if (positionKey(node.fen) !== node.positionKey) {
      issues.push({
        code: "fen-key",
        message: `node ${key} FEN does not match positionKey`,
      });
    }
    const unknown = unknownConceptTags(node.concepts);
    if (unknown.length) {
      issues.push({
        code: "concept",
        message: `node ${key} unknown concepts: ${unknown.join(", ")}`,
      });
    }
    const chess = new Chess(node.fen);
    const legal = new Set(chess.moves());
    const seenSan = new Set<string>();
    for (const move of node.moves) {
      if (seenSan.has(move.san)) {
        issues.push({
          code: "duplicate-edge",
          message: `node ${key}: duplicate SAN ${move.san}`,
        });
      }
      seenSan.add(move.san);
      const probe = new Chess(node.fen);
      const played = safeMove(probe, move.san);
      if (!played || !legal.has(played.san)) {
        issues.push({
          code: "illegal",
          message: `node ${key}: ${move.san} is illegal`,
        });
        continue;
      }
      const uci = played.from + played.to + (played.promotion || "");
      if (uci !== move.uci) {
        issues.push({
          code: "uci",
          message: `node ${key}: ${move.san} uci ${move.uci} !== ${uci}`,
        });
      }
      const nextKey = positionKey(probe.fen());
      if (nextKey !== move.nextPositionKey) {
        issues.push({
          code: "edge-target",
          message: `node ${key}: ${move.san} should land on ${nextKey}`,
        });
      }
      if (!course.nodes[move.nextPositionKey]) {
        issues.push({
          code: "orphan-edge",
          message: `node ${key}: ${move.san} points at missing ${move.nextPositionKey}`,
        });
      }
    }
  }

  const reachable = new Set<string>();
  const stack = [course.rootPositionKey];
  while (stack.length) {
    const key = stack.pop()!;
    if (reachable.has(key)) continue;
    reachable.add(key);
    const node = course.nodes[key];
    if (!node) continue;
    for (const move of node.moves) stack.push(move.nextPositionKey);
  }
  for (const key of Object.keys(course.nodes)) {
    if (!reachable.has(key)) {
      issues.push({ code: "unreachable", message: `node ${key} is unreachable from root` });
    }
  }

  const path: string[] = [];
  let cursor = course.nodes[course.rootPositionKey];
  const seenPath = new Set<string>();
  while (cursor && !seenPath.has(cursor.positionKey)) {
    seenPath.add(cursor.positionKey);
    const next = cursor.moves.find((m) => m.role === "repertoire" || m.role === "mainline");
    if (!next) break;
    path.push(next.san);
    cursor = course.nodes[next.nextPositionKey];
  }
  const needed = course.targetFullMoves * 2;
  const endedOnHandoff = Boolean(cursor?.handoff);
  if (path.length < needed && !endedOnHandoff) {
    issues.push({
      code: "coverage",
      message: `repertoire is ${path.length} plies; need ${needed} or an explicit handoff`,
    });
  }

  return issues;
}

export function repertoireSans(node: LessonNode): LessonMove[] {
  return node.moves.filter((m) => m.role === "repertoire" || m.role === "mainline");
}
